import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ToasterSeverities } from "@core/enums/toaster-severities";
import { CompositionModelHelper } from "@core/helpers/composition-model.helper";
import { DialogHelper } from "@core/helpers/dialog-helper";
import { BandwidthAssetRisk } from "@core/models/bandwidth-asset-risk.model";
import { BandwidthFundRisk } from "@core/models/bandwidth-fund-risk.model";
import { Composition } from "@core/models/composition.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { CompositionsInfo } from "@core/models/compositions-info.model";
import { FundComposition } from "@core/models/fund-composition.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { CompositionModelService } from "@core/services/composition-model.service";
import { NotificationService } from "@core/services/notification.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  mergeMap,
  Observable,
  of,
  switchMap,
} from "rxjs";

import * as compositionModelActions from "./composition-model.actions";

@Injectable()
export class CompositionModelEffects {
  loadCompositionModelList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.LOAD_COMPOSITION_MODEL_LIST),
      mergeMap((action: { searchTerm?: string }): Observable<Action> => {
        this.store.dispatch(
          compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
            loading: true,
          })
        );
        return this.compositionModelService
          .getCompositionModelList$(action.searchTerm)
          .pipe(
            switchMap(
              (
                compositionModelList: Array<Composition>
              ): Observable<Action> => {
                this.store.dispatch(
                  compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of(
                  compositionModelActions.SET_COMPOSITION_MODEL_LIST({
                    compositionModelList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  saveCompositionModel$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.SAVE_COMPOSITION_MODEL),
      mergeMap(
        (action: {
          compositionModel: Partial<Composition>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(
            compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
              loading: true,
            })
          );
          return this.compositionModelService
            .saveCompositionModel$(action.compositionModel, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                // Names the model, and says whether it is new or changed.
                this.notificationService.show(
                  ToasterSeverities.Success,
                  action.id
                    ? "COMPOSITION_MODEL_UPDATED"
                    : "COMPOSITION_MODEL_CREATED",
                  undefined,
                  { name: action.compositionModel.name ?? "" }
                );
                return of(
                  compositionModelActions.LOAD_COMPOSITION_MODEL_LIST({})
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "COMPOSITION_MODEL_SAVE_ERROR",
                  response,
                  { name: action.compositionModel.name ?? "" }
                );
                return of(
                  compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  saveCompositionModelVersion$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.SAVE_COMPOSITION_MODEL_VERSION),
      concatMap(
        (action: {
          compositionModelVersion: Partial<CompositionVersion>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(
            compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
              loading: true,
            })
          );
          return this.compositionModelService
            .saveCompositionModelVersion$(
              action.compositionModelVersion,
              action.id
            )
            .pipe(
              concatMap(
                (compositionModelVersionId: string): Observable<Action> => {
                  const fundCompostions: Array<FundComposition> =
                    CompositionModelHelper.addModelVersionId(
                      action.compositionModelVersion.fundCompositions!,
                      compositionModelVersionId ?? action.id
                    ) as Array<FundComposition>;
                  return this.compositionModelService
                    .saveFundCompositionList$(fundCompostions, action.id)
                    .pipe(
                      concatMap((): Observable<Action> => {
                        this.dialog.closeAll();
                        this.notificationService.show(
                          ToasterSeverities.Success,
                          "COMPOSITION_VERSION_SAVED"
                        );
                        return this.saveBandwidthRisks$(
                          action.compositionModelVersion,
                          compositionModelVersionId ?? action.id
                        );
                      }),
                      catchError(
                        (response: HttpErrorResponse): Observable<Action> => {
                          this.notificationService.show(
                            ToasterSeverities.Error,
                            "FUND_COMPOSITION_SAVE_ERROR",
                            response
                          );
                          return of(
                            compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS(
                              { loading: false }
                            )
                          );
                        }
                      )
                    );
                }
              ),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "COMPOSITION_VERSION_SAVE_ERROR",
                  response
                );
                return of(
                  compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  cancelCompositionModelVersion$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.CANCEL_COMPOSITION_MODEL_VERSION),
      mergeMap((action: { versionId: string }): Observable<Action> => {
        this.store.dispatch(
          compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
            loading: true,
          })
        );
        return this.compositionModelService
          .cancelCompositionModelVersion$(action.versionId)
          .pipe(
            switchMap((): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Success,
                "COMPOSITION_VERSION_CANCELLED"
              );
              return of(
                compositionModelActions.LOAD_COMPOSITION_MODEL_LIST({})
              );
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "CANCEL_COMPOSITION_VERSION_ERROR",
                response
              );
              return of(
                compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  loadCompositionModelListInfo$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.LOAD_COMPOSITION_MODEL_LIST_INFO),
      mergeMap((): Observable<Action> => {
        return this.compositionModelService.getCompositionModelListInfo$().pipe(
          switchMap(
            (compositionListInfo: CompositionsInfo): Observable<Action> => {
              return of(
                compositionModelActions.SET_COMPOSITION_MODEL_LIST_INFO({
                  compositionListInfo,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadProducFundList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.LOAD_PRODUCT_FUND_LIST),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.compositionModelService.getProductFundList$(action.id).pipe(
          switchMap(
            (productFundList: Array<FundWithRelated>): Observable<Action> => {
              return of(
                compositionModelActions.SET_PRODUCT_FUND_LIST({
                  productFundList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadRiskProfilesList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.LOAD_RISK_PROFILES_LIST),
      mergeMap((): Observable<Action> => {
        return this.compositionModelService.getRiskProfilesList$().pipe(
          switchMap(
            (riskProfilesList: Array<RiskProfile>): Observable<Action> => {
              return of(
                compositionModelActions.SET_RISK_PROFILES_LIST({
                  riskProfilesList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadCompositionModel$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(compositionModelActions.LOAD_COMPOSITION_MODEL),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.compositionModelService
          .getCompositionModel$(action.id)
          .pipe(
            switchMap((compositionModel: Composition): Observable<Action> => {
              return of(
                compositionModelActions.SET_COMPOSITION_MODEL({
                  compositionModel,
                })
              );
            }),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  /**
   * Persists the version's drift bands after the fund compositions are saved.
   * A version registers drift either per fund (BandwidthFundRisk) or per asset
   * class (BandwidthAssetRisk) depending on the model's portfolio-drift type;
   * only the active array is present in the payload.
   */
  private saveBandwidthRisks$(
    compositionModelVersion: Partial<CompositionVersion>,
    versionId: string
  ): Observable<Action> {
    if (compositionModelVersion.bandwidthFundRisks?.length) {
      const bandwidthFundRisks: Array<BandwidthFundRisk> =
        CompositionModelHelper.addModelVersionId(
          compositionModelVersion.bandwidthFundRisks,
          versionId
        ) as Array<BandwidthFundRisk>;
      return this.compositionModelService
        .saveBandwidthFundRiskList$(bandwidthFundRisks)
        .pipe(
          switchMap((): Observable<Action> => {
            return of(compositionModelActions.LOAD_COMPOSITION_MODEL_LIST({}));
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "FUND_RISK_SAVE_ERROR",
              response
            );
            return of(
              compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                loading: false,
              })
            );
          })
        );
    }
    if (compositionModelVersion.bandwidthAssetRisks?.length) {
      const bandwidthAssetRisks: Array<BandwidthAssetRisk> =
        CompositionModelHelper.addModelVersionId(
          compositionModelVersion.bandwidthAssetRisks,
          versionId
        ) as Array<BandwidthAssetRisk>;
      return this.compositionModelService
        .saveBandwidthAssetRiskList$(bandwidthAssetRisks)
        .pipe(
          switchMap((): Observable<Action> => {
            return of(compositionModelActions.LOAD_COMPOSITION_MODEL_LIST({}));
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "ASSET_RISK_SAVE_ERROR",
              response
            );
            return of(
              compositionModelActions.SET_COMPOSITION_MODEL_LOADING_STATUS({
                loading: false,
              })
            );
          })
        );
    }
    return of(compositionModelActions.LOAD_COMPOSITION_MODEL_LIST({}));
  }

  constructor(
    private actions$: Actions,
    private compositionModelService: CompositionModelService,
    private notificationService: NotificationService,
    private store: Store,
    private dialog: DialogHelper
  ) {}
}
